'use client'

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { usePathname } from 'next/navigation'
import { useAuth } from '@/features/auth'
import {
  DISMISSAL_COOKIE,
  PENDING_WELCOME_KEY,
  WELCOME_COOLDOWN_MS,
  WELCOME_DELAY_MS,
  type GatedAction,
  type WelcomeSource,
} from './app-invite.constants'
import { recordWelcomeDismissal } from './dismissal.actions'
import { isMobileWeb, isNativeAppClient } from './platform'

/**
 * Central state for the Web → App invite (PB-303). One instance lives near the
 * app root; both the modal and every trigger call-site read from it so only one
 * prompt can be visible at a time.
 *
 * Trigger rules (from the card):
 *  - `showWelcome` — after sign-in / sign-up / hive-join. Respects a per-user
 *    dismissal cooldown persisted in a cookie (syncs across web sessions).
 *  - `showGated` — when a logged-in user attempts a web-unsupported action.
 *    Ignores the welcome cooldown, but is rate-limited to once per action-type
 *    per session so it never stacks or spams.
 */

type ActivePrompt =
  | { kind: 'welcome'; source: WelcomeSource }
  | { kind: 'gated'; action: GatedAction }
  | null

interface AppInviteContextValue {
  active: ActivePrompt
  /** True inside the native app — the modal must never render there. */
  suppressed: boolean
  /** Returns whether the welcome actually displayed (false if suppressed, in
   *  cooldown, or another prompt is already showing) so callers can react. */
  showWelcome: (source: WelcomeSource) => boolean
  showGated: (action: GatedAction) => void
  dismiss: () => void
}

const AppInviteContext = createContext<AppInviteContextValue | undefined>(undefined)

function readDismissalMap(): Record<string, number> {
  if (typeof document === 'undefined') return {}
  const match = document.cookie
    .split('; ')
    .find((row) => row.startsWith(`${DISMISSAL_COOKIE}=`))
  if (!match) return {}
  try {
    const value = decodeURIComponent(match.slice(DISMISSAL_COOKIE.length + 1))
    const parsed = JSON.parse(value) as Record<string, number>
    return parsed && typeof parsed === 'object' ? parsed : {}
  } catch {
    return {}
  }
}

/**
 * Write the dismissal into `document.cookie` synchronously so `readDismissalMap`
 * reflects it immediately. The `recordWelcomeDismissal` server action is the
 * durable writer (and would eventually set the same cookie), but it's async and
 * fire-and-forget; without this a welcome re-triggered in the same tick could
 * slip through before the Set-Cookie response lands.
 *
 * Kept attribute-symmetric with the server writer (`dismissal.actions.ts`): same
 * cooldown-based pruning to bound the map on shared machines, same `Secure` gate
 * (`NODE_ENV === 'production'`, inlined into the client bundle by Next), so the
 * two never write divergent cookies for the same name/path.
 */
function writeDismissalClient(userId: string, dismissedAt: number) {
  if (typeof document === 'undefined') return
  const map = readDismissalMap()
  map[userId] = dismissedAt
  const cutoff = dismissedAt - WELCOME_COOLDOWN_MS
  for (const [id, ts] of Object.entries(map)) {
    if (ts < cutoff) delete map[id]
  }
  const maxAgeSeconds = Math.ceil((WELCOME_COOLDOWN_MS * 2) / 1000)
  const secure = process.env.NODE_ENV === 'production' ? '; Secure' : ''
  document.cookie =
    `${DISMISSAL_COOKIE}=${encodeURIComponent(JSON.stringify(map))}` +
    `; path=/; max-age=${maxAgeSeconds}; SameSite=Lax${secure}`
}

/**
 * Start the welcome cooldown for this user: write it client-side (so the gate
 * sees it at once) and durably via the server action. Called whenever a welcome
 * prompt is retired — whether the user dismissed it, or a higher-intent gated
 * prompt displaced it (a displaced welcome was still "shown", so it must start
 * its cooldown, else the next trigger re-shows it and defeats the 3-day gate).
 */
function recordWelcomeCooldown(userId: string) {
  const now = Date.now()
  writeDismissalClient(userId, now)
  void recordWelcomeDismissal(userId, now)
}

export function AppInviteProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth()
  const userId = user?.id ?? null

  const [active, setActive] = useState<ActivePrompt>(null)
  const [suppressed, setSuppressed] = useState(false)

  // In-app-webview detection is client-only; resolve after mount so SSR and the
  // first client render agree (both start `false`).
  useEffect(() => {
    setSuppressed(isNativeAppClient())
  }, [])

  const welcomeAllowedByCooldown = useCallback(() => {
    if (!userId) return false
    const dismissedAt = readDismissalMap()[userId]
    if (!dismissedAt) return true
    return Date.now() - dismissedAt >= WELCOME_COOLDOWN_MS
  }, [userId])

  const showWelcome = useCallback(
    (source: WelcomeSource): boolean => {
      // Returns whether the welcome was actually shown, so callers (the pending
      // flag consumer) only mark it consumed when it truly displayed.
      // Never replace a prompt that's already up (one-at-a-time) — the gated
      // variant is higher-intent, so a welcome never displaces it. Decide with
      // the current `active` snapshot so the updater itself stays pure.
      if (isNativeAppClient()) return false
      // Mobile-web only: the welcome nudge is "get the app", which is nonsense
      // on a desktop that can't run it (V1 sprint test #11). Desktop's only
      // invite is the contextual gated prompt, so `showGated` is intentionally
      // NOT gated here. iPad-as-desktop UAs fall through to desktop (no prompt),
      // which is the safe default for the welcome nudge.
      if (!isMobileWeb()) return false
      if (active) return false
      if (!welcomeAllowedByCooldown()) return false
      setActive((current) => (current ? current : { kind: 'welcome', source }))
      return true
    },
    [active, welcomeAllowedByCooldown]
  )

  const showGated = useCallback(
    (action: GatedAction) => {
      if (isNativeAppClient()) return
      // Displacing a standing welcome counts as retiring it (it was shown), so
      // start its cooldown here — otherwise the welcome is swallowed silently and
      // re-shows on the next trigger, defeating the 3-day gate. Side effect is
      // outside the updater (updaters must stay pure).
      if (active?.kind === 'welcome' && userId) {
        recordWelcomeCooldown(userId)
      }
      // Every explicit attempt at a web-unsupported action re-raises the prompt:
      // it's the sole entry point (the web product form was replaced), so a click
      // must always respond. The functional updater keeps it one-at-a-time — it
      // won't stack a duplicate if the same gated prompt is already open — which
      // is what the card's "never stacks or spams" guards against. The gated
      // variant is contextual/high-intent, so it displaces a standing welcome and
      // ignores the welcome cooldown (AC4).
      setActive((current) => {
        if (current?.kind === 'gated' && current.action === action) return current
        return { kind: 'gated', action }
      })
    },
    [active, userId]
  )

  const dismiss = useCallback(() => {
    // Dismissing a welcome starts its per-user cooldown so it stays down across
    // sessions. Side effect lives outside the state updater (updaters must stay
    // pure — React can invoke them twice in StrictMode).
    if (active?.kind === 'welcome' && userId) {
      recordWelcomeCooldown(userId)
    }
    setActive(null)
  }, [active, userId])

  const value = useMemo<AppInviteContextValue>(
    () => ({ active, suppressed, showWelcome, showGated, dismiss }),
    [active, suppressed, showWelcome, showGated, dismiss]
  )

  return <AppInviteContext.Provider value={value}>{children}</AppInviteContext.Provider>
}

export function useAppInvite(): AppInviteContextValue {
  const ctx = useContext(AppInviteContext)
  if (!ctx) throw new Error('useAppInvite must be used within an AppInviteProvider')
  return ctx
}

/**
 * Consumes the one-shot `pending-welcome` flag left by the post-auth navigation.
 *
 * Auth completes with a client `router.push`, and this consumer is mounted once
 * at the persistent root (it never remounts on a soft nav). So we can't rely on
 * a mount-time read — the flag is written *after* the initial mount. Instead we
 * re-check whenever the route changes (`pathname`, which does update on soft
 * nav) or the user finishes hydrating (`userId`). The sessionStorage flag itself
 * is the idempotency guard: it's removed only once we actually fire, so a read
 * that lands before the user has hydrated leaves the flag in place to fire on
 * the next tick rather than dropping the welcome (critic gaps #3/#4).
 *
 * The welcome does not fire synchronously: once the flag is seen it is scheduled
 * ~`WELCOME_DELAY_MS` later (V1 sprint test #11 — an immediate prompt over an
 * unseen page is jarring). The delay timer is cleared on unmount and re-armable
 * if a scheduled fire is declined.
 */
export function usePendingWelcome() {
  const { showWelcome } = useAppInvite()
  const { user } = useAuth()
  const userId = user?.id ?? null
  const pathname = usePathname()

  // Guards against re-scheduling the delay timer on every soft-nav re-run: once
  // a timer is pending for this consumer's lifetime we don't restart the clock
  // (which would otherwise keep pushing the welcome out on each route change).
  // Reset only when a scheduled fire declines to display, so a later re-run
  // (e.g. a gated prompt was dismissed) can re-arm it.
  const scheduledRef = useRef(false)

  useEffect(() => {
    // Wait until the user has hydrated — showWelcome's cooldown gate needs a
    // userId, and firing before then would silently no-op and lose the flag.
    if (!userId) return
    if (typeof sessionStorage === 'undefined') return
    if (scheduledRef.current) return

    let source: string | null = null
    try {
      source = sessionStorage.getItem(PENDING_WELCOME_KEY)
    } catch {
      return
    }
    if (source !== 'sign-in' && source !== 'sign-up' && source !== 'join-hive') return
    const pendingSource = source

    // Delay the welcome rather than firing the instant the destination mounts:
    // an immediate "get the app" prompt over a page the user hasn't seen yet is
    // jarring (V1 sprint test #11). The sessionStorage flag remains the
    // idempotency guard — we only clear it once the welcome truly displays.
    scheduledRef.current = true
    const timer = setTimeout(() => {
      // Re-check happens inside showWelcome: cooldown, one-at-a-time, native,
      // and mobile-web are all re-evaluated at fire time, so a state change
      // during the delay (a gated prompt opened, cooldown was set) is respected.
      // Only consume the flag if the welcome actually displayed; otherwise leave
      // it and re-arm so a later trigger (e.g. a gated prompt was dismissed,
      // which re-identifies showWelcome and re-runs this effect) can retry.
      if (showWelcome(pendingSource)) {
        try {
          sessionStorage.removeItem(PENDING_WELCOME_KEY)
        } catch {
          // Couldn't clear it — showWelcome's one-at-a-time guard still prevents
          // a visible duplicate on the next re-run.
        }
      } else {
        scheduledRef.current = false
      }
    }, WELCOME_DELAY_MS)

    return () => {
      clearTimeout(timer)
      // The pending timer was torn down (unmount / dependency change) before it
      // fired, so allow the next run to re-schedule.
      scheduledRef.current = false
    }
  }, [showWelcome, userId, pathname])
}

/** Sets the one-shot flag the destination page consumes via `usePendingWelcome`. */
export function markPendingWelcome(source: WelcomeSource) {
  try {
    sessionStorage.setItem(PENDING_WELCOME_KEY, source)
  } catch {
    // No sessionStorage → the welcome just won't fire; not worth failing auth.
  }
}
